import { Node, mergeAttributes } from "@tiptap/core";
import { fetchAttachmentBlob } from "@/api/taskAttachments";
import { kbImageUrl } from "@/api/knowledge";

/**
 * D-147 W1 S4 — TipTap-нода content_image. JSON-канон збігається з
 * whitelist-рендерером §7 і скан-хелпером KbContentImages S1:
 * {type:'image', attrs:{attachmentId, alt}} — src у contentJson НЕ живе.
 *
 * Дельта S1-P3a: щойно завантажене зображення ДО першого draftSave недоступне
 * навіть editor-у (формула §3: imageId ∈ draft ∨ published contentJson; ACL
 * НЕ розширюємо) → превʼю до save = local-blob (реєстр нижче). Після save
 * зображення вже в draft-JSON — nodeView тягне authorized blob (bare <img>
 * = 401, D-131).
 */

const localPreviews = new Map<string, string>();

/** Реєструє local-blob превʼю щойно завантаженого зображення (до draftSave). */
export function registerKbImagePreview(attachmentId: string, objectUrl: string): void {
  localPreviews.set(attachmentId, objectUrl);
}

/** Revoke усіх object-URL (unmount редактора). */
export function releaseKbImagePreviews(): void {
  for (const url of localPreviews.values()) URL.revokeObjectURL(url);
  localPreviews.clear();
}

export interface KbImageOptions {
  itemId: string;
}

export const KbImage = Node.create<KbImageOptions>({
  name: "image",
  group: "block",
  atom: true,
  draggable: true,

  addOptions() {
    return { itemId: "" };
  },

  addAttributes() {
    return {
      attachmentId: { default: null },
      alt: { default: "" },
    };
  },

  parseHTML() {
    return [
      {
        tag: "img[data-attachment-id]",
        getAttrs: (el) => ({
          attachmentId: (el as HTMLElement).getAttribute("data-attachment-id"),
          alt: (el as HTMLElement).getAttribute("alt") ?? "",
        }),
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "img",
      mergeAttributes({
        "data-attachment-id": HTMLAttributes.attachmentId as string,
        alt: (HTMLAttributes.alt as string) || "",
      }),
    ];
  },

  addNodeView() {
    const itemId = this.options.itemId;

    return ({ node }) => {
      const img = document.createElement("img");
      img.alt = (node.attrs.alt as string) || "";
      img.className = "my-2.5 max-w-full rounded-lg border";
      img.setAttribute("data-attachment-id", (node.attrs.attachmentId as string) || "");

      const attachmentId = node.attrs.attachmentId as string | null;
      if (attachmentId) {
        const local = localPreviews.get(attachmentId);
        if (local) {
          img.src = local;
        } else {
          fetchAttachmentBlob(kbImageUrl(itemId, attachmentId))
            .then((blob) => {
              const url = URL.createObjectURL(blob);
              localPreviews.set(attachmentId, url); // reuse + один revoke-цикл
              img.src = url;
            })
            .catch(() => {
              // deny/збій → alt-плейсхолдер без битого запиту
            });
        }
      }

      return { dom: img };
    };
  },
});
